import { ValidateRequired } from 'validation';
import ValidateDefaults from 'validation/ValidateDefaults';
import ValidateEmail from 'validation/ValidateEmail';
import ValidateFullName from 'validation/ValidateFullName';
import ValidatePassword from 'validation/ValidatePassword';
import ValidateLength from 'validation/ValidateLength';
import ValidateConfirm from 'validation/ValidateConfirm';
import { sprintf } from 'sprintf-js';
import ValidateRecurlyHosted from 'validation/ValidateRecurlyHosted';
import ValidateZip from 'validation/ValidateZip';
import ValidateBirthDate from 'validation/ValidateBirthDate';
import { getLocale } from 'services/LocalizationService';

export default class ValidationService {
    constructor(fields, errorMessages) {
        /**
         * User Defined Validations passed in from a FormConfig.formFieldList
         *
         * @type {Array}
         * @private
         */
        this._userDefinedValidations = fields || {};

        /**
         * Validators mapped from _userDefinedValidations
         *
         * @type {Object}
         */
        this.validations = {};
        //{
        //      formfieldName: [Validator, Validator, ...]
        // }

        //[];

        this._errorMessages = errorMessages;

        // this.isValid = null;
        // this.message = null;

        // map _userDefinedValidations to validations
        this.setup();
    }

    setup() {
        for (let i in this._userDefinedValidations) {
            if (!this._userDefinedValidations.hasOwnProperty(i)) continue;
            let formField = this._userDefinedValidations[i];
            formField.validation.forEach((validator) => {
                let fieldName = formField.name;
                let label = formField.validationLabel || formField.label;
                if (typeof validator === 'string') {
                    this.getValidationByString(validator, fieldName, label);
                } else if (validator instanceof Array) {
                    this.getValidationByString(validator[0], fieldName, label, validator.slice(1));
                } else if (typeof validator === 'object') {
                    this.setValidationByObject(validator, fieldName);
                }
            });
        }
    }

    getValidationByString(str, validationKey, label, options) {
        let oValidator;

        switch (str) {
            case 'required':
                oValidator = new ValidateRequired({});

                oValidator.message = () => {
                    let errorMessage =
                        this._errorMessages['validation_required_' + validationKey.toLowerCase()] ||
                        this._errorMessages.validation_required ||
                        oValidator.constructor.defaultMessage(label);

                    return sprintf(errorMessage, label);
                };

                break;
            case 'full_name':
                oValidator = new ValidateFullName({});

                this.assignValidationErrorMessages(oValidator, oValidator.pageAttrKey, label);
                break;

            case 'email':
                oValidator = new ValidateEmail({});

                this.assignValidationErrorMessages(oValidator, oValidator.pageAttrKey, label);

                break;
            case 'password':
                oValidator = new ValidatePassword({});

                this.assignValidationErrorMessages(oValidator, oValidator.pageAttrKey, label);
                break;
            case 'length':
                oValidator = new ValidateLength({
                    options: options,
                });

                oValidator.message = () => {
                    let errorMessage =
                        this._errorMessages.validation_length ||
                        oValidator.constructor.defaultMessage();

                    return sprintf(errorMessage, label, options[0]);
                };

                break;
            case 'zip_country':
                // TODO: validate against country, not needed yet as we don't have zip in Canada sign up
                oValidator = new ValidateZip({
                    options: options,
                });

                this.assignValidationErrorMessages(oValidator, oValidator.pageAttrKey, label);

                break;
            case 'match_with':
                let matcher = options[0];
                let matcherLabel = this._userDefinedValidations[matcher].label || matcher;

                oValidator = new ValidateConfirm({
                    options,
                });

                oValidator.message = () => {
                    const errorMessage =
                        this._errorMessages.validation_match ||
                        getLocale('email_addresses_must_match');
                    return sprintf(errorMessage, label, matcherLabel);
                };

                break;
            case 'recurly_hosted':
                oValidator = new ValidateRecurlyHosted({});

                oValidator.message = () => {
                    let errorMessage =
                        this._errorMessages.validation_recurly_hosted ||
                        oValidator.constructor.defaultMessage();

                    return sprintf(errorMessage, label);
                };

                break;
            case 'birthdate':
                oValidator = new ValidateBirthDate({});

                this.assignValidationErrorMessages(oValidator, oValidator.pageAttrKey, label);

                break;
            default:
                oValidator = new ValidateDefaults({});
        }

        this.setValidationByObject(oValidator, validationKey);
    }

    setValidationByObject(oValidator, validationKey) {
        if (!this.validations[validationKey]) {
            this.validations[validationKey] = [];
        }

        this.validations[validationKey].push(oValidator);
    }

    // get multiple errors if available or use single/default message
    assignValidationErrorMessages(oValidator, validationKey, label) {
        oValidator.message = () => {
            // check if field has multiple validation options
            let matchingValidations = this.matchingValidationErrors(validationKey);

            // if it does, save those for later parsing
            if (matchingValidations.length > 1) {
                let matchingValidationMessage = oValidator.getMatchingValidationMessage(
                    validationKey,
                    matchingValidations,
                );

                let errorMessage =
                    this._errorMessages[matchingValidationMessage] ||
                    oValidator.constructor.defaultMessage();

                return sprintf(errorMessage, label);
            } else {
                // otherwise use old method
                let errorMessage =
                    this._errorMessages[validationKey] || oValidator.constructor.defaultMessage();
                return sprintf(errorMessage, label);
            }
        };
    }

    matchingValidationErrors(validationKey) {
        let keys = Object.keys(this._errorMessages);

        let matchingKeys = [];

        keys.forEach((key) => {
            if (key.includes(validationKey)) {
                matchingKeys.push(key);
            }
        });

        return matchingKeys;
    }

    /**
     * @returns {{isValid: *, message: *, displayErrorState: (boolean|*)}|{isValid: boolean}}
     */
    validateOne({ name, on, formData }) {
        if (typeof this.validations[name] === 'object') {
            for (let validation of this.validations[name]) {
                if (
                    validation instanceof ValidateConfirm &&
                    validation.getSecondFieldName() in formData
                ) {
                    validation.setSecondFieldValue(formData[validation.getSecondFieldName()]);
                }

                let isValid = validation.validate(formData[name]);
                validation.setFormData(formData[name]);

                // Return first validation failure
                if (!isValid) {
                    return {
                        isValid,
                        get message() {
                            return validation.getMessage(on);
                        },
                        // message: validation.getMessage(on),
                    };
                }
            }
        }

        return {
            isValid: true,
        };
    }

    /**
     * @param oFormData
     * @returns {{isValid: boolean, message: string, errors: *}}
     */
    validateAll(oFormData) {
        let res = {
            isValid: true,
            message: '',
            errors: new Map(),
        };

        for (let validationKey in this.validations) {
            let r = this.validateOne({
                name: validationKey,
                value: oFormData[validationKey],
                formData: oFormData,
            });

            if (!r.isValid) {
                res.isValid = false;
                res.message = 'something is not valid, check results';

                res.errors.set(validationKey, r.message);
            }
        }

        return res;
    }

    // this could be used as the form error / success handler
    // as opposed to each input

    // getIsValid() {
    //     return this.isValid;
    // }
    //
    // getMessage() {
    //     return this.message;
    // }
}
